import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ApiError, unwrap } from '@/api/envelope';
import { deleteCategory, type DocumentCategoryRow } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { StatCard, StatCardRow } from '@/components/ui/StatCard';
import { useToast } from '@/components/ui/useToast';

import { CategoryDialog } from './CategoryDialog';
import { useCategories, useInvalidateDocuments, type CategoryRow } from './useDocuments';
import { useDocumentsWritable } from './useDocumentsWritable';

/** "1 document" or "n documents". */
function documents(count: number): string {
  return `${count} ${count === 1 ? 'document' : 'documents'}`;
}

/** The row as the dialog takes it: the backend's shape, which it edits. */
function asBackendRow(row: CategoryRow): DocumentCategoryRow {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    document_count: row.documentCount,
  };
}

/**
 * The Categories page (PB10, PB11, P15, P16): the three totals, the
 * categories with their counts, Rename and Delete on each real one, and
 * New Category. Uncategorized is listed last and cannot be changed, because
 * it is not a row the backend keeps but every document without one.
 *
 * Nothing is predicted (P14): every write waits for the backend, then the
 * whole Documents section is asked for again, because the library's tabs
 * and the upload dialog's select carry the same names and counts.
 */
export function CategoriesPage() {
  const { writable, statusForbids, statusWord } = useDocumentsWritable();

  const refresh = useInvalidateDocuments();
  const toast = useToast();
  const query = useCategories();

  // A fresh dialog for every category, so nothing typed last time is shown;
  // the row stays while the dialog fades out, so it does not go blank.
  const [editing, setEditing] = useState<CategoryRow | null>(null);
  const [dialogKey, setDialogKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<CategoryRow | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Stable, so the memoised columns do not change on every render.
  const openDialog = useCallback((row: CategoryRow | null) => {
    setEditing(row);
    setDialogKey((n) => n + 1);
    setDialogOpen(true);
  }, []);

  const remove = useMutation({
    mutationFn: async (row: CategoryRow) => {
      const reply = await deleteCategory({
        path: { category_id: row.id as string },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onSuccess: (_reply, row) => {
      // The count the dialog promised and the page shows, which leaves out
      // removed documents; the reply's `documents_moved` counts those too,
      // and two numbers for one action would contradict each other.
      toast.show({
        message: `“${row.name}” was deleted. ${documents(row.documentCount)} moved to Uncategorized.`,
      });
    },
    onError: (error) => {
      // A 404, a 403 or anything else: the backend's words, then the truth.
      toast.show({
        message:
          (error instanceof ApiError ? error.serverMessage : null) ??
          'That category could not be deleted. Please try again.',
        severity: 'error',
      });
    },
    onSettled: async () => {
      setDeleteOpen(false);
      await refresh();
    },
  });

  const rows = useMemo<CategoryRow[]>(
    () => (query.data ? [...query.data.categories, query.data.uncategorized] : []),
    [query.data],
  );

  const columns = useMemo<Column<CategoryRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Category',
        render: (row) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.name}
            </Typography>
            {row.description ? (
              <Typography variant="body2" color="text.secondary">
                {row.description}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        key: 'documents',
        header: 'Documents',
        align: 'right',
        render: (row) => (
          <Typography variant="body2">{documents(row.documentCount)}</Typography>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        align: 'right',
        render: (row) =>
          writable && !row.isUncategorized ? (
            <RowActionsMenu
              rowLabel={row.name}
              testId="document-categories-row-actions"
              actions={[
                { id: 'rename', label: 'Rename', onSelect: () => openDialog(row) },
                {
                  id: 'delete',
                  label: 'Delete',
                  onSelect: () => {
                    setDeleting(row);
                    setDeleteOpen(true);
                  },
                },
              ]}
            />
          ) : null,
      },
    ],
    [writable, openDialog],
  );

  const totals = query.data?.totals;
  // A failed read leaves no numbers to show; a placeholder that never fills
  // in would read as a page still arriving, so the cards show a dash instead.
  const total = (count: number | undefined) => (query.isError ? '—' : (count ?? 0));
  const noCategories = query.isSuccess && query.data.categories.length === 0;

  const newButton = (testId: string) =>
    writable ? (
      <Button onClick={() => openDialog(null)} data-testid={testId}>
        New Category
      </Button>
    ) : null;

  return (
    <Box data-testid="document-categories-page">
      <PageHeader
        title="Categories"
        description="Organize the document library. Every document belongs to one category, or to none."
        action={newButton('document-categories-new-button')}
      />

      {statusForbids ? (
        <Alert
          severity="warning"
          role="alert"
          data-testid="document-categories-read-only-alert"
          sx={{ mb: 3, maxWidth: 760 }}
        >
          This organization is {statusWord}, so its categories cannot be changed. Contact
          Activist Technologies to discuss it.
        </Alert>
      ) : null}

      <StatCardRow columns={3} testId="document-categories-summary-group">
        <StatCard
          label="Categories"
          value={total(totals?.categories)}
          caption="Named by your organization"
          loading={query.isPending}
          testId="document-categories-categories-card"
        />
        <StatCard
          label="Documents"
          value={total(totals?.documents)}
          caption="In the library, not counting removed ones"
          loading={query.isPending}
          testId="document-categories-documents-card"
        />
        <StatCard
          label="Uncategorized"
          value={total(totals?.uncategorized)}
          caption="Documents in no category"
          loading={query.isPending}
          testId="document-categories-uncategorized-card"
        />
      </StatCardRow>

      {noCategories ? (
        <EmptyState
          title="No categories yet."
          description="A category groups documents under one name in the library and the ARC."
          action={newButton('document-categories-new-first-button')}
          testId="document-categories"
        />
      ) : (
        <DataTable
          caption="Categories"
          testId="document-categories-table"
          columns={columns}
          rows={rows}
          getRowKey={(row) => row.id ?? 'uncategorized'}
          loading={query.isPending}
          refreshing={query.isFetching && !query.isPending}
          error={query.isError ? 'The categories could not be loaded.' : null}
          onRetry={() => void query.refetch()}
        />
      )}

      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="document-categories-footnote-message"
        sx={{ mt: 2, maxWidth: 760 }}
      >
        Deleting a category keeps its documents: they move to Uncategorized and stay in
        the library.
      </Typography>

      <CategoryDialog
        key={dialogKey}
        open={dialogOpen}
        category={editing ? asBackendRow(editing) : null}
        onClose={() => setDialogOpen(false)}
        onSaved={() => undefined}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={deleting ? `Delete “${deleting.name}”?` : 'Delete?'}
        description={
          deleting
            ? `${documents(deleting.documentCount)} move to Uncategorized. The category cannot be restored.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        pending={remove.isPending}
        testId="document-categories-delete-dialog"
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
}
